import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Autocomplete,
  Tooltip,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";

import DataTable from "theme/Tables/DataTable";
import { useAwaitDialog } from "app/components/status_dialog";

const formatNumber = (value) => Number(value || 0).toLocaleString();

function StatCard({ icon, title, value }) {
  return (
    <Card sx={{ height: "100%", marginTop: "0px" }}>
      <MDBox p={2}>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
          <MDBox>
            <MDTypography variant="h6" color="text" fontWeight="light">
              {title}
            </MDTypography>

            <MDTypography variant="h4" fontWeight="medium" mt={1}>
              {formatNumber(value)}
            </MDTypography>
          </MDBox>

          <MDBox
            width={48}
            height={48}
            borderRadius="lg"
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgColor="dark"
            color="white"
            shadow="md"
          >
            <Icon fontSize="medium">{icon}</Icon>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default function InventoryTrackerPage() {
  const { ref } = useParams();
  const navigate = useNavigate();

  const title = "Inventory Tracker";
  usePageTitle(title);

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [tracker, setTracker] = useState(null);
  const [summary, setSummary] = useState({});
  const [products, setProducts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [supervisors, setSupervisors] = useState([]);

  const [openSupervisorDialog, setOpenSupervisorDialog] = useState(false);
  const [openIssueDialog, setOpenIssueDialog] = useState(false);
  const [openIssueAgentDialog, setOpenIssueAgentDialog] = useState(false);

  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [supervisorAgent, setSupervisorAgent] = useState("");

  const [issueProduct, setIssueProduct] = useState("");
  const [issueQuantity, setIssueQuantity] = useState("");

  const [accountAgents, setAccountAgents] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);

  const [openRecallDialog, setOpenRecallDialog] = useState(false);

  const [recallProduct, setRecallProduct] = useState("");
  const [recallQuantity, setRecallQuantity] = useState("");

  const productColumns = [
    { Header: "Product", accessor: "name", align: "left" },
    { Header: "Issued", accessor: "issued", align: "center" },
    { Header: "Unsold", accessor: "available", align: "center" },
    { Header: "Locked", accessor: "locked", align: "center" },
    { Header: "Sold", accessor: "consumed", align: "center" },
    // { Header: "Adjusted", accessor: "adjusted", align: "center" },
    { Header: "Returned", accessor: "returned", align: "right" },
  ];

  const supervisorColumns = [
    { Header: "Supervisor", accessor: "name", align: "left" },
    { Header: "Unsold", accessor: "available", align: "center" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const agentColumns = [
    { Header: "Agent", accessor: "name", align: "left" },
    { Header: "Issued", accessor: "issued", align: "center" },
    { Header: "Unsold", accessor: "available", align: "center" },
    { Header: "Locked", accessor: "locked", align: "center" },
    { Header: "Sold", accessor: "consumed", align: "center" },
    // { Header: "Adjusted", accessor: "adjusted", align: "center" },
    { Header: "Returned", accessor: "returned", align: "right" },
  ];

  const buildProductRows = (data) => {
    const rows = [];

    data.map((row) => {
      rows.push({
        name: (
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="button" fontWeight="medium">
              {row.name}
            </MDTypography>
          </MDBox>
        ),
        issued: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {formatNumber(row.issued)}
          </MDTypography>
        ),
        locked: (
          <MDTypography variant="caption" color="warning" fontWeight="medium">
            {formatNumber(row.locked)}
          </MDTypography>
        ),
        consumed: (
          <MDTypography variant="caption" color="success" fontWeight="medium">
            {formatNumber(row.consumed)}
          </MDTypography>
        ),
        returned: (
          <MDTypography variant="caption" color="info" fontWeight="medium">
            {formatNumber(row.returned)}
          </MDTypography>
        ),
        // adjusted: (
        //   <MDTypography variant="caption" color="text" fontWeight="medium">
        //     {formatNumber(row.adjusted)}
        //   </MDTypography>
        // ),
        available: (
          <MDTypography variant="caption" color="dark" fontWeight="bold">
            {formatNumber(row.available)}
          </MDTypography>
        ),
      });

      return null;
    });

    return rows;
  };

  const buildSupervisorRows = (data) => {
    const rows = [];

    data.map((row) => {
      rows.push({
        name: row.name,
        available: formatNumber(row.available),
        actions: (
          <MDBox display="flex" justifyContent="flex-end" gap={1}>
            <Tooltip title="Issue Stock">
              <MDButton
                iconOnly
                size="small"
                variant="outlined"
                color="info"
                fontWeight="medium"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  setSelectedSupervisor(row);
                  setOpenIssueDialog(true);
                }}
              >
                <Icon>add</Icon>
              </MDButton>
            </Tooltip>
            <Tooltip title="Recall Stock">
              <MDButton
                iconOnly
                size="small"
                variant="outlined"
                color="warning"
                fontWeight="medium"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  setSelectedSupervisor(row);
                  setOpenRecallDialog(true);
                }}
              >
                <Icon>undo</Icon>
              </MDButton>
            </Tooltip>
            <Tooltip title="Manage Supervisor">
              <MDButton
                iconOnly
                size="small"
                variant="outlined"
                color="dark"
                onClick={() => navigate(`/inventory/trackers/supervisors/${row.reference}`)}
              >
                <Icon>settings</Icon>
              </MDButton>
            </Tooltip>
            <Tooltip title="Remove Supervisor">
              <MDButton iconOnly size="small" variant="outlined" color="error">
                <Icon>delete</Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
      });

      return null;
    });

    return rows;
  };

  const buildAgentRows = (data) => {
    const rows = [];

    data.map((row) => {
      rows.push({
        name: (
          <MDTypography
            component="a"
            variant="button"
            color="info"
            fontWeight="medium"
            sx={{ cursor: "pointer" }}
            onClick={() => navigate(`/inventory/trackers/${ref}/agents/${row.reference}`)}
          >
            {row.name}
          </MDTypography>
        ),
        available: (
          <MDTypography variant="caption" color="dark" fontWeight="bold">
            {formatNumber(row.available)}
          </MDTypography>
        ),
        issued: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {formatNumber(row.issued)}
          </MDTypography>
        ),
        locked: (
          <MDTypography variant="caption" color="warning" fontWeight="medium">
            {formatNumber(row.locked)}
          </MDTypography>
        ),
        consumed: (
          <MDTypography variant="caption" color="success" fontWeight="medium">
            {formatNumber(row.consumed)}
          </MDTypography>
        ),
        // adjusted: (
        //   <MDTypography variant="caption" color="text" fontWeight="medium">
        //     {formatNumber(row.adjusted)}
        //   </MDTypography>
        // ),
        returned: (
          <MDTypography variant="caption" color="info" fontWeight="medium">
            {formatNumber(row.returned)}
          </MDTypography>
        ),
      });

      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(`/portal/products/inventory/trackers/${ref}/fetch.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setTracker(result.data.data.tracker);
            setSummary(result.data.data.summary || {});
            setProducts(buildProductRows(result.data.data.products || []));
            setAgents(buildAgentRows(result.data.data.agents || []));
            setSupervisors(buildSupervisorRows(result.data.data.supervisors || []));

            setAccountAgents(result.data.data.account_agents || []);
            setAvailableProducts(result.data.data.account_products || []);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [ref, refreshPage]);

  async function saveSupervisor() {
    if (!supervisorAgent) {
      setSeverity("error");
      setErrMsg("Please select a supervisor.");
      setOpen(true);
      return;
    }

    setOpenSupervisorDialog(false);
    setShowLoading(true);

    await axios
      .post(`/portal/products/inventory/trackers/supervisors/create.json`, {
        reference: ref,
        agent: supervisorAgent,
      })
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          setSupervisorAgent("");
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("Unable to complete request.");
        setOpen(true);
      });
  }

  async function saveIssueStock() {
    if (!issueProduct || !issueQuantity) {
      setSeverity("error");
      setErrMsg("Please select a product and quantity.");
      setOpen(true);
      return;
    }

    setOpenIssueDialog(false);
    setShowLoading(true);

    await axios
      .post("/portal/products/inventory/trackers/supervisors/stocks/issue.json", {
        reference: selectedSupervisor.reference,
        product: issueProduct,
        quantity: parseInt(issueQuantity, 10),
      })
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          setIssueProduct("");
          setIssueQuantity("");
          setSelectedSupervisor(null);
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("Unable to complete request.");
        setOpen(true);
      });
  }

  async function saveRecallStock() {
    if (!recallProduct || !recallQuantity) {
      setSeverity("error");
      setErrMsg("Please select a product and quantity.");
      setOpen(true);
      return;
    }

    setOpenRecallDialog(false);
    setShowLoading(true);

    await axios
      .post("/portal/products/inventory/trackers/supervisors/stocks/recall.json", {
        reference: selectedSupervisor.reference,
        product: recallProduct,
        quantity: parseInt(recallQuantity, 10),
      })
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          setRecallProduct("");
          setRecallQuantity("");
          setSelectedSupervisor(null);
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("Unable to complete request.");
        setOpen(true);
      });
  }

  async function issueAgentStock() {
    if (!supervisorAgent) {
      setSeverity("error");
      setErrMsg("Please select an agent.");
      setOpen(true);
      return;
    }

    if (!issueProduct || !issueQuantity) {
      setSeverity("error");
      setErrMsg("Please select a product and quantity.");
      setOpen(true);
      return;
    }

    setOpenIssueAgentDialog(false);
    setShowLoading(true);

    await axios
      .post("/portal/products/inventory/trackers/agents/stocks/issue.json", {
        reference: ref,
        agent: supervisorAgent,
        product: issueProduct,
        quantity: parseInt(issueQuantity, 10),
      })
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          setIssueProduct("");
          setIssueQuantity("");
          setSelectedSupervisor(null);
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("Unable to complete request.");
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openRecallDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Recall Stock</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <MDTypography variant="button" fontWeight="medium">
              {selectedSupervisor?.name}
            </MDTypography>
          </MDBox>

          <MDBox mt={3}>
            <MDInput
              select
              label="Product"
              fullWidth
              value={recallProduct}
              onChange={(e) => setRecallProduct(e.target.value)}
              SelectProps={{ style: { padding: "12px 0px" } }}
            >
              {availableProducts.map((row) => (
                <MenuItem key={row.reference} value={row.reference}>
                  {row.title}
                </MenuItem>
              ))}
            </MDInput>
          </MDBox>

          <MDBox mt={3}>
            <MDInput
              label="Quantity"
              type="number"
              fullWidth
              value={recallQuantity}
              onChange={(e) => setRecallQuantity(e.target.value)}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenRecallDialog(false);
              setRecallProduct("");
              setRecallQuantity("");
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={saveRecallStock}>
            Recall
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openSupervisorDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Add Supervisor</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            {/* <MDInput
              select
              label="Supervisor"
              fullWidth
              value={supervisorAgent}
              onChange={(e) => setSupervisorAgent(e.target.value)}
              SelectProps={{ style: { padding: "12px 0px" } }}
            >
              {accountAgents.map((row) => (
                <MenuItem key={row.reference} value={row.reference}>
                  {row.title}
                </MenuItem>
              ))}
            </MDInput> */}
            <Autocomplete
              options={accountAgents}
              getOptionLabel={(option) => option.title}
              isOptionEqualToValue={(option, value) => option.reference === value.reference}
              onChange={(event, value) => {
                setSupervisorAgent(value ? value.reference : null);
              }}
              renderInput={(params) => <MDInput {...params} label="Select Agent" fullWidth />}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenSupervisorDialog(false);
              setSupervisorAgent("");
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={saveSupervisor}>
            Add
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openIssueDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Issue Stock</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <MDTypography variant="button" fontWeight="medium">
              {selectedSupervisor?.name}
            </MDTypography>
          </MDBox>

          <MDBox mt={3}>
            <MDInput
              select
              label="Product"
              fullWidth
              value={issueProduct}
              onChange={(e) => setIssueProduct(e.target.value)}
              SelectProps={{ style: { padding: "12px 0px" } }}
            >
              {availableProducts.map((row) => (
                <MenuItem key={row.reference} value={row.reference}>
                  {row.title}
                </MenuItem>
              ))}
            </MDInput>
          </MDBox>

          <MDBox mt={3}>
            <MDInput
              label="Quantity"
              type="number"
              fullWidth
              value={issueQuantity}
              onChange={(e) => setIssueQuantity(e.target.value)}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenIssueDialog(false);
              setIssueProduct("");
              setIssueQuantity("");
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={saveIssueStock}>
            Issue
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openIssueAgentDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Issue Agent Stock</MDBox>
        </DialogTitle>

        <DialogContent>
          <Autocomplete
            options={accountAgents}
            getOptionLabel={(option) => option.title}
            isOptionEqualToValue={(option, value) =>
              option.agent_reference === value.agent_reference
            }
            onChange={(event, value) => {
              setSupervisorAgent(value ? value.agent_reference : null);
            }}
            renderInput={(params) => <MDInput {...params} label="Select Agent" fullWidth />}
          />

          <MDBox mt={3}>
            <MDInput
              select
              label="Product"
              fullWidth
              value={issueProduct}
              onChange={(e) => setIssueProduct(e.target.value)}
              SelectProps={{ style: { padding: "12px 0px" } }}
            >
              {availableProducts.map((row) => (
                <MenuItem key={row.reference} value={row.reference}>
                  {row.title}
                </MenuItem>
              ))}
            </MDInput>
          </MDBox>

          <MDBox mt={3}>
            <MDInput
              label="Quantity"
              type="number"
              fullWidth
              value={issueQuantity}
              onChange={(e) => setIssueQuantity(e.target.value)}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenIssueAgentDialog(false);
              setIssueProduct("");
              setIssueQuantity("");
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={issueAgentStock}>
            Issue
          </MDButton>
        </DialogActions>
      </Dialog>

      {pageLoaded && tracker && (
        <MDBox mb={5}>
          <MDBox mt={0} mb={1} display="flex" justifyContent="space-between">
            <MDBox>
              <Button
                color="dark"
                size="small"
                startIcon={<Icon>west</Icon>}
                onClick={() => navigate(`/inventory/trackers`)}
              >
                Back
              </Button>
            </MDBox>

            {/* <MDBox display="flex" gap={1}>
              <Button color="dark" size="small" startIcon={<Icon>add</Icon>}>
                Assign Stock
              </Button>

              <Button color="dark" size="small" startIcon={<Icon>undo</Icon>}>
                Record Return
              </Button>
            </MDBox> */}
          </MDBox>

          <MDBox mt={2}>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={2}>
                <MDBox align="center">
                  <MDTypography variant="h6" fontSize={48}>
                    <Icon>track_changes</Icon>
                  </MDTypography>

                  <MDTypography variant="h4" fontWeight="medium">
                    {tracker.title}
                  </MDTypography>

                  <MDTypography variant="button" color="text" fontWeight="light" mt={1}>
                    {tracker.object_type.toUpperCase()} • {tracker.status ? "ACTIVE" : "DIABLED"}
                  </MDTypography>

                  {/* <MDBox mt={3} display="flex" justifyContent="center" gap={4} flexWrap="wrap">
                    <MDBox textAlign="center">
                      <MDTypography variant="button" color="text" fontWeight="light">
                        Issued
                      </MDTypography>
                      <MDTypography variant="h5" fontWeight="medium">
                        {formatNumber(summary.issued)}
                      </MDTypography>
                    </MDBox>

                    <MDBox textAlign="center">
                      <MDTypography variant="button" color="text" fontWeight="light">
                        Locked
                      </MDTypography>
                      <MDTypography variant="h5" fontWeight="medium">
                        {formatNumber(summary.locked)}
                      </MDTypography>
                    </MDBox>

                    <MDBox textAlign="center">
                      <MDTypography variant="button" color="text" fontWeight="light">
                        Sold
                      </MDTypography>
                      <MDTypography variant="h5" fontWeight="medium">
                        {formatNumber(summary.consumed)}
                      </MDTypography>
                    </MDBox>

                    <MDBox textAlign="center">
                      <MDTypography variant="button" color="text" fontWeight="light">
                        Returned
                      </MDTypography>
                      <MDTypography variant="h5" fontWeight="medium">
                        {formatNumber(summary.returned)}
                      </MDTypography>
                    </MDBox>

                    <MDBox textAlign="center">
                      <MDTypography variant="button" color="text" fontWeight="light">
                        Available
                      </MDTypography>
                      <MDTypography variant="h5" fontWeight="medium">
                        {formatNumber(summary.available)}
                      </MDTypography>
                    </MDBox>
                  </MDBox> */}
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>

          <MDBox mt={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={4}>
                <StatCard icon="redo" title="Issued" value={summary.issued} />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <StatCard icon="inventory" title="Unsold" value={summary.available} />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <StatCard icon="lock" title="Locked" value={summary.locked} />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <StatCard icon="shopping_cart" title="Sold" value={summary.consumed} />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <StatCard icon="move_down" title="Transfered" value={summary.transfered} />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <StatCard icon="undo" title="Returned" value={summary.returned} />
              </Grid>
            </Grid>
          </MDBox>

          <MDBox mt={3}>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Products
                </MDTypography>
              </MDBox>

              <DataTable
                table={{ columns: productColumns, rows: products }}
                isSorted
                entriesPerPage={false}
                showTotalEntries
                buttons
                noEndBorder
              />
            </Card>
          </MDBox>

          <MDBox mt={3}>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="medium">
                  Supervisors
                </MDTypography>

                <MDBox>
                  <MDButton
                    variant="contained"
                    color="dark"
                    iconOnly
                    size="small"
                    onClick={() => setOpenSupervisorDialog(true)}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                  </MDButton>
                </MDBox>
              </MDBox>

              <DataTable
                table={{ columns: supervisorColumns, rows: supervisors }}
                isSorted
                entriesPerPage={false}
                showTotalEntries
                buttons
                noEndBorder
              />
            </Card>
          </MDBox>

          <MDBox mt={3}>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={2} display="flex" justifyContent="space-between">
                <MDTypography variant="h6" fontWeight="medium">
                  Agent Stock Positions
                </MDTypography>
                <MDBox display="flex" gap={1}>
                  <MDButton
                    color="dark"
                    size="small"
                    startIcon={<Icon>add</Icon>}
                    variant="outlined"
                    onClick={() => {
                      setSupervisorAgent(null);
                      setOpenIssueAgentDialog(true);
                    }}
                  >
                    Assign Stock
                  </MDButton>
                </MDBox>
              </MDBox>

              <DataTable
                table={{ columns: agentColumns, rows: agents }}
                isSorted
                entriesPerPage={false}
                showTotalEntries
                buttons
                noEndBorder
              />
            </Card>
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}
